import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Card, Empty, InputNumber, List, Segmented, Switch, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { listMyThresholdBreaches, listMyThresholds, upsertMyThreshold } from '@app/features/doctorPortal/api';
import BreachGroupControls from '@app/features/doctorPortal/BreachGroupControls';
import BreachGroupHeader from '@app/features/doctorPortal/BreachGroupHeader';
import { breachFilterOptions, groupBreaches, type BreachLabels } from '@app/features/doctorPortal/breachGrouping';
import { readBreachGroupPrefs, toggleGroup, writeBreachGroupPrefs } from '@app/features/doctorPortal/breachGroupPrefs';
import {
  METRIC_LABEL_KEYS,
  METRIC_UNITS,
  type DoctorThreshold,
  type ThresholdBreach,
  type VitalMetric,
} from '@app/features/doctorPortal/types';
import { useCallback, useMemo, useState } from 'react';

/**
 * Measurements outside the doctor's own bounds.
 *
 * Breaches are DERIVED server-side from the vitals already on each visit —
 * nothing is stored and nothing is acknowledged. That is what makes this screen
 * self-correcting: raise a bound and the matching rows are gone on the next
 * fetch; correct a mistyped measurement and the alert corrects with it, because
 * there is only ever one copy of the value.
 *
 * It also means there is no "acknowledge" action to build. A breach stops being
 * shown when it stops being true.
 *
 * THE LIST ARRIVES FOLDED. Three hundred rows of one reading each is not a
 * screen anyone reads; grouped by patient and collapsed, the same data is a page
 * of names with a count beside each. Patient, measurement and visit are all
 * offered because they answer different questions — who needs me, what is
 * drifting, what happened on that day — and the grouping is remembered per
 * browser (breachGroupPrefs).
 */

const num = (value: string | number) => (typeof value === 'number' ? value : Number(value));

const DoctorMeasurementsPage = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [days, setDays] = useState<number>(30);
  // Bumped when a save fails, to remount the bound controls. They keep what was
  // typed in their own state, and the threshold query did not change, so nothing
  // else would put the saved value back on screen — leaving a number that reads
  // as an alerting bound the doctor set when the server never took it.
  const [boundsKey, setBoundsKey] = useState(0);
  // Read once, on mount: localStorage is not reactive and re-reading it on every
  // render would fight the user's own clicks.
  const [groupPrefs, setGroupPrefs] = useState(readBreachGroupPrefs);

  const persistPrefs = useCallback((next: typeof groupPrefs) => {
    setGroupPrefs(next);
    writeBreachGroupPrefs(next);
  }, []);

  const thresholds = useQuery({
    queryKey: ['doctor', 'thresholds'],
    queryFn: listMyThresholds,
  });

  const breaches = useQuery({
    queryKey: ['doctor', 'breaches', days],
    queryFn: () => listMyThresholdBreaches({ days, limit: 300 }),
  });

  const save = useMutation({
    mutationFn: upsertMyThreshold,
    onSuccess: () => {
      // Both, and in this order: the bounds changed, so the derived list did too.
      void queryClient.invalidateQueries({ queryKey: ['doctor', 'thresholds'] });
      void queryClient.invalidateQueries({ queryKey: ['doctor', 'breaches'] });
    },
    onError: () => {
      message.error(t('doctor-threshold-save-failed'));
      setBoundsKey((key) => key + 1);
    },
  });

  const commit = (row: DoctorThreshold, patch: Partial<DoctorThreshold>) => {
    const next = { ...row, ...patch };
    save.mutate({
      metric: next.metric,
      lowValue: next.lowValue == null ? null : num(next.lowValue),
      highValue: next.highValue == null ? null : num(next.highValue),
      isEnabled: next.isEnabled,
    });
  };

  const metricLabel = (metric: VitalMetric) => t(METRIC_LABEL_KEYS[metric]);

  const labels: BreachLabels = useMemo(
    () => ({
      metric: (metric) => t(METRIC_LABEL_KEYS[metric]),
      patient: (breach) => `${breach.clientLastName ?? ''} ${breach.clientFirstName ?? ''}`.trim(),
      date: (iso) => new Date(iso).toLocaleDateString(i18n.language),
    }),
    [t, i18n.language]
  );

  const rows = useMemo(() => breaches.data ?? [], [breaches.data]);
  const grouped = useMemo(() => groupBreaches(rows, groupPrefs, labels), [rows, groupPrefs, labels]);
  const filterOptions = useMemo(
    () => breachFilterOptions(rows, groupPrefs.groupBy, labels),
    [rows, groupPrefs.groupBy, labels]
  );

  const renderBreach = (breach: ThresholdBreach) => (
    <List.Item key={`${breach.visitId}-${breach.metric}`}>
      <List.Item.Meta
        title={
          <span className="flex flex-wrap items-center gap-2">
            <Typography.Text strong>
              {breach.clientLastName} {breach.clientFirstName}
            </Typography.Text>
            <Typography.Text type="secondary">{metricLabel(breach.metric)}</Typography.Text>
            <Typography.Text type="danger" strong>
              {num(breach.value).toLocaleString(i18n.language)} {METRIC_UNITS[breach.metric]}
            </Typography.Text>
            <Tag className="!m-0">
              {t(breach.side === 'high' ? 'doctor-breach-above' : 'doctor-breach-below', {
                limit: num(breach.limitValue).toLocaleString(i18n.language),
              })}
            </Tag>
          </span>
        }
        description={new Date(breach.measuredAt).toLocaleString(i18n.language)}
      />
    </List.Item>
  );

  if (thresholds.isError || breaches.isError) {
    return <Alert type="error" showIcon message={t('doctor-measurements-load-failed')} />;
  }

  return (
    // The layout's content area is `overflow-hidden` and every page owns its own
    // scrolling, or everything past the fold is clipped with no scrollbar.
    <div className="flex-1 min-h-0 overflow-y-auto grid grid-cols-1 xl:grid-cols-3 gap-4 items-start">
      <Card
        className="xl:col-span-2"
        title={t('doctor-breaches-title', { count: grouped.rows.length })}
        extra={
          <Segmented
            size="small"
            value={days}
            onChange={(value) => setDays(value as number)}
            options={[
              { label: t('doctor-window-30'), value: 30 },
              { label: t('doctor-window-90'), value: 90 },
              { label: t('doctor-window-180'), value: 180 },
            ]}
          />
        }
      >
        <div className="mb-2">
          <BreachGroupControls
            prefs={groupPrefs}
            onPrefsChange={persistPrefs}
            filterOptions={filterOptions}
            disabled={breaches.isLoading}
          />
        </div>

        {groupPrefs.groupBy === 'none' ? (
          <List
            loading={breaches.isLoading}
            dataSource={grouped.rows}
            locale={{ emptyText: <Empty description={t('doctor-breaches-empty')} /> }}
            renderItem={renderBreach}
          />
        ) : (
          <List
            loading={breaches.isLoading}
            dataSource={grouped.sections}
            locale={{ emptyText: <Empty description={t('doctor-breaches-empty')} /> }}
            split={false}
            renderItem={(section) => (
              <div key={section.id} className="border-b border-slate-100 last:border-0">
                <BreachGroupHeader
                  name={section.name}
                  subtitle={section.subtitle}
                  count={section.breaches.length}
                  collapsed={section.collapsed}
                  onToggle={() => persistPrefs(toggleGroup(groupPrefs, section.id))}
                />
                {/* Unmounted rather than hidden: a folded group of 300 rows that
                    is still in the DOM costs exactly what folding was meant to
                    save. */}
                {!section.collapsed && (
                  <div className="pl-8">
                    <List dataSource={section.breaches} renderItem={renderBreach} />
                  </div>
                )}
              </div>
            )}
          />
        )}
      </Card>

      <Card title={t('doctor-thresholds-title')} loading={thresholds.isLoading}>
        <Typography.Paragraph type="secondary" className="!text-xs">
          {t('doctor-thresholds-help')}
        </Typography.Paragraph>
        <div className="flex flex-col gap-3">
          {(thresholds.data ?? []).map((row) => (
            <div key={`${row.metric}-${boundsKey}`} className="flex items-center gap-2">
              <Switch
                size="small"
                checked={row.isEnabled}
                onChange={(checked) => commit(row, { isEnabled: checked })}
              />
              <div className="min-w-0 flex-1">
                <div className="text-xs truncate">{metricLabel(row.metric)}</div>
                <div className="text-[10px] text-slate-400">
                  {METRIC_UNITS[row.metric]}
                  {/* A metric the doctor has never touched is alerting on the
                      clinical default, not on nothing — say so, because silence
                      reads as "off". */}
                  {row.isDefault ? ` · ${t('doctor-threshold-default')}` : ''}
                </div>
              </div>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                disabled={!row.isEnabled}
                value={row.lowValue == null ? null : num(row.lowValue)}
                step={row.metric === 'TEMPERATURE' ? 0.1 : 1}
                onBlur={(e) => commit(row, { lowValue: e.target.value === '' ? null : Number(e.target.value) })}
              />
              <span className="text-slate-300 text-xs">–</span>
              <InputNumber
                size="small"
                style={{ width: 72 }}
                disabled={!row.isEnabled}
                value={row.highValue == null ? null : num(row.highValue)}
                step={row.metric === 'TEMPERATURE' ? 0.1 : 1}
                onBlur={(e) => commit(row, { highValue: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default DoctorMeasurementsPage;
